// A tiny history router: three routes do not justify a dependency.
import { createElement, useEffect, useState } from 'react';
import type { AnchorHTMLAttributes, MouseEvent } from 'react';

export type Route =
  | { name: 'home' }
  | { name: 'room'; slug: string }
  | { name: 'meeting'; id: string; atMs: number | null }
  | { name: 'not_found' };

const SLUG_RE = /^[a-z0-9-]{1,32}$/;
const NAV_EVENT = 'kima:navigate';

export function parseRoute(pathname: string, search = ''): Route {
  const parts = pathname.split('/').filter(Boolean).map(safeDecode);
  if (parts.length === 0) return { name: 'home' };
  const [head, value, ...rest] = parts;
  if (rest.length > 0 || value === undefined || value === null) return { name: 'not_found' };
  if (head === 'r' && SLUG_RE.test(value)) return { name: 'room', slug: value };
  if (head === 'm' && value.length > 0) {
    const t = new URLSearchParams(search).get('t');
    const at = t !== null && /^\d{1,10}$/.test(t) ? Number(t) : null;
    return { name: 'meeting', id: value, atMs: at };
  }
  return { name: 'not_found' };
}

function safeDecode(part: string): string | null {
  try {
    return decodeURIComponent(part);
  } catch {
    return null;
  }
}

export const paths = {
  home: () => '/',
  room: (slug: string) => `/r/${encodeURIComponent(slug)}`,
  /** `atMs`: a meeting-timeline ms; the page selects the loaded segment containing it. */
  meeting: (id: string, atMs?: number | null) =>
    `/m/${encodeURIComponent(id)}${atMs !== undefined && atMs !== null ? `?t=${Math.max(0, Math.round(atMs))}` : ''}`,
};

export function navigate(to: string): void {
  window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(NAV_EVENT));
}

function current(): Route {
  return parseRoute(window.location.pathname, window.location.search);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(current);
  useEffect(() => {
    const update = () => setRoute(current());
    window.addEventListener('popstate', update);
    window.addEventListener(NAV_EVENT, update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener(NAV_EVENT, update);
    };
  }, []);
  return route;
}

export function Link(props: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { onClick, ...rest } = props;
  return createElement('a', {
    ...rest,
    onClick(event: MouseEvent<HTMLAnchorElement>) {
      onClick?.(event);
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      navigate(props.href);
    },
  });
}
